import React, { useState } from 'react';
import axios from 'axios';
import { StickyNote, Phone, Users, Loader2, Plus, CalendarPlus } from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const KINDS = [['note', 'Note', StickyNote], ['call', 'Call', Phone], ['meeting', 'Meeting', Users]];
const OUTCOMES = { call: ['reached', 'voicemail', 'no answer', 'interested', 'not interested', 'callback requested'], meeting: ['measured', 'proposal presented', 'samples left', 'follow-up needed'] };

export const QuickActivityBar = ({ customerId, token, onAdded }) => {
  const [kind, setKind] = useState('note');
  const [text, setText] = useState('');
  const [outcome, setOutcome] = useState('');
  const [duration, setDuration] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/admin/customers/${customerId}/activities`,
        { kind, text: text.trim(), outcome: outcome || null, duration_min: duration ? Number(duration) : null, follow_up_at: followUp ? new Date(followUp).toISOString() : null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${KINDS.find((k) => k[0] === kind)[1]} logged`);
      setText(''); setOutcome(''); setDuration(''); setFollowUp(''); setOpen(false);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="quick-activity-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-neutral-100 rounded-lg p-0.5">
          {KINDS.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => { setKind(k); setOutcome(''); setOpen(true); }} className={`px-2.5 h-7 text-xs rounded inline-flex items-center gap-1 ${kind === k ? 'bg-white shadow text-neutral-900' : 'text-neutral-500'}`} data-testid={`activity-kind-${k}`}><Icon className="w-3.5 h-3.5" />{label}</button>
          ))}
        </div>
        {!open && <button type="button" onClick={() => setOpen(true)} className="flex-1 text-left h-8 px-3 rounded-md border border-dashed border-neutral-300 text-xs text-neutral-500 hover:border-amber-400 hover:text-neutral-800" data-testid="activity-open">Add a quick note or log a call…</button>}
      </div>
      {open && (
        <div className="space-y-2">
          <Textarea autoFocus rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save(); }}
            placeholder={kind === 'call' ? 'What did you talk about?' : kind === 'meeting' ? 'Meeting summary…' : 'Write a note…'} className="text-sm resize-none" data-testid="activity-text" />
          <div className="flex items-center gap-2 flex-wrap">
            {OUTCOMES[kind] && (
              <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-7 rounded-md border border-neutral-200 px-2 text-xs" data-testid="activity-outcome">
                <option value="">Outcome…</option>{OUTCOMES[kind].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {kind !== 'note' && <input type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="min" className="h-7 w-16 rounded-md border border-neutral-200 px-2 text-xs" data-testid="activity-duration" />}
            <label className="inline-flex items-center gap-1 text-[11px] text-neutral-500"><CalendarPlus className="w-3.5 h-3.5" />Follow up<input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="h-7 rounded-md border border-neutral-200 px-1.5 text-xs" data-testid="activity-follow-up" /></label>
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setText(''); }} className="h-7 text-xs">Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy || !text.trim()} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 gap-1" data-testid="activity-save">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}Add to timeline</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActivityBar;
